import "./index.scss";

import React from "react";
import { useTranslation } from "react-i18next";
import { PageProps, graphql } from "gatsby";

import { usePageLocalization } from "@sbi-international/boutik.gatsby.plugin.multilocale";

import Slider, { SliderData } from "../components/cmsBlocks/Slider";

import SEO from "../components/Seo";

import { SEOArguments } from "../classes/seoArguments";
import { LocalType } from "../models/localType";
import { Articles, ContentBox, Section } from "../components/cmsBlocks";
import MainLayout from "../components/layout/MainLayout";

export const query = graphql`
    query IndexPage {
        articles: allProductsJson(filter: { category: { eq: "/Century/CMS/Blog/" } }) {
            nodes {
                category
                code
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                images
                fields {
                    thumbnail {
                        childImageSharp {
                            gatsbyImageData(placeholder: NONE, layout: FULL_WIDTH)
                        }
                    }
                    thumbnails {
                        childImageSharp {
                            gatsbyImageData(placeholder: NONE, layout: FULL_WIDTH)
                        }
                    }
                }
                attributes {
                    blog_subject {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    content_short {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    visible_on_home_page {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        apropos: allProductsJson(filter: { code: { eq: "century_accueil-a-propos" } }) {
            nodes {
                code
                attributes {
                    content {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    texte_1 {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    texte_2 {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    bouttons {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    url {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        carrousel: allProductsJson(filter: { category: { eq: "/Century/CMS/Carousel/" } }) {
            nodes {
                category
                code
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                images
                fields {
                    ...productsFieldsImagesFragment
                }
                attributes {
                    content {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    bouttons {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    url {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
        metadata: allProductsJson(filter: { code: { eq: "century_accueil" } }) {
            nodes {
                code
                attributes {
                    meta_title {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    meta_description {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

export default function IndexPage({
    data,
}: PageProps<Queries.IndexPageQuery>) {
    const carrouselData = data.carrousel.nodes;
    const apropos = data.apropos.nodes[0];
    
    const { currentLocale } = usePageLocalization();

    const seoArguments = new SEOArguments(data.metadata, currentLocale as keyof LocalType);

    const { t } = useTranslation("mainLayout");
    const config = {
        siteHeader: {
            showAnnouncementBar: true,
        },
        mainPadding: "0",
    };

    let slidersData: SliderData[] = [];

    carrouselData.forEach((data: any) => {
        const sliderButtons: any[] = data!.attributes!.bouttons!.values!.map(
            (button: LocalType) => button![currentLocale]
        );

        const img = data?.fields?.images?.[0]?.childImageSharp?.gatsbyImageData;

        const title: string = data.title![currentLocale];

        const p: string = data.attributes!.content!.values![0]![currentLocale];

        const slider_url: string = data.attributes!.url!.values![0]![currentLocale];

        slidersData.push({
            img: img,
            title: title,
            buttons: sliderButtons,
            p: p,
            url: slider_url,
        });
    });

    const contentBlocks1 = [
        {
            title: t("contentblock1-title1"),
            p: t("contentblock1-content1"),
            background: {
                type: "source",
                src: "installateur-bg.jpg",
            },
            searchGroup: {
                placeholder: t("contentblock1-placeholder1"),
                buttonLabel: ">",
            },
        },
        {
            title: t("contentblock1-title2"),
            p: t("contentblock1-content2"),
            buttonLabel: t("contentblock1-button2"),
            buttonLink: "/promotions/",
            background: {
                type: "source",
                src: "promotions-bg.jpg",
            },
            btnAlign: "marginTop",
        },
    ];

    const contentBlocks2 = [
        {
            title: apropos?.attributes?.texte_1?.values?.[0]?.[currentLocale] ?? '',
            subtitle: apropos?.attributes?.texte_2?.values?.[0]?.[currentLocale] ?? '',
            align: "center",
            background: {
                type: "source",
                src: "century-notre-famille.jpg",
            },
            p: apropos?.attributes?.content?.values?.[0]?.[currentLocale] ?? '',
            buttonLabel: apropos?.attributes?.bouttons?.values?.[0]?.[currentLocale] ?? '',
            buttonLink: apropos?.attributes?.url?.values?.[0]?.[currentLocale] ?? '',
        },
    ];

    const section2 = {
        align: "center",
        title: t("section2-title"),
        subtitle: t("section2-subtitle"),
    };
    
    return (
        <MainLayout config={config}>
            <SEO title={seoArguments.title} description={seoArguments.description} />
            <Slider slides={slidersData} />
            <ContentBox blocks={contentBlocks1} />

            <Section section={section2}>
                <Articles
                    articles={data.articles.nodes.filter(
                        (article: any) =>
                            article.attributes?.visible_on_home_page?.values[0]?.fr_CA === "Oui"
                    )}
                />
            </Section>

            <ContentBox blocks={contentBlocks2} />
        </MainLayout>
    );
}
